import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { withExperience } from '@helpcenter/analytics-components';
import { ExperienceName } from '@helpcenter/constants';
import type { RouterContext } from 'react-resource-router';
import type { PortalAnnouncement } from 'rest/portal';
import type { State } from 'state';
import { fetchRequestCreateAction } from 'state/actions/request-create';
import { fetchUiModificationsAction } from 'state/actions/ui-modifications';
import {
	getPortalAnnouncement,
	getName,
	getProjectId,
	getIsProjectSimplified,
	getCanAdministerProject,
} from 'state/selectors/portal';
import { getRequestCreateOrError, getFormName } from 'state/selectors/request-create';
import { getCanAdministerJIRA, isLoggedIn as isLoggedInSelector } from 'state/selectors/user';
import * as qs from '@atlassian/help-center-common-util/history-qs';
import RequestCreateDumb from './request-create-page';
import type { PersistedError } from 'state/persisted/types';

type ExternalProps = RouterContext;

type Props = ExternalProps & {
	fetchRequestCreate: typeof fetchRequestCreateAction;
	fetchUiModifications: typeof fetchUiModificationsAction;
	isLoading: boolean;
	error: PersistedError | undefined;
	announcement: PortalAnnouncement | undefined;
	projectId: number;
	portalName: string | undefined;
	requestTypeName: string | undefined;
	isProjectSimplified: boolean;
	isProjectAdmin: boolean;
	isLoggedIn: boolean;
};

interface RequestCreatePageState {
	sideBarOpen: boolean;
}

const toNumber = (idFromPath: string) => {
	return idFromPath ? Number(idFromPath) : undefined;
};

class RequestCreatePage extends React.Component<Props, RequestCreatePageState> {
	state = {
		sideBarOpen: !!qs.get('customize'),
	};

	toggleSideBar = (value?: boolean) => {
		this.setState({ sideBarOpen: value !== undefined ? value : !this.state.sideBarOpen });
	};

	/**
	 * Returns true when UI modifications should be fetched.
	 */
	private canFetchUiModifications(
		portalId: string | number | null | undefined,
		requestTypeId: string | number | null | undefined,
	): boolean {
		return !!requestTypeId && !!portalId;
	}

	componentDidMount() {
		const { requestTypeId, portalId } = this.props.match.params;
		this.props.fetchRequestCreate({
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			portalId: toNumber(portalId!)!,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			id: toNumber(requestTypeId!)!,
		});
		if (this.canFetchUiModifications(portalId, requestTypeId)) {
			this.props.fetchUiModifications({
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				portalId: toNumber(portalId!)!,
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				requestTypeId: toNumber(requestTypeId!)!,
			});
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { fetchRequestCreate, fetchUiModifications } = this.props;
		const { portalId, requestTypeId } = this.props.match.params;
		const { requestTypeId: prevRequestId } = prevProps.match.params;

		const requestChanged = requestTypeId && requestTypeId !== prevRequestId;
		if (requestChanged) {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			fetchRequestCreate({ portalId: toNumber(portalId!)!, id: toNumber(requestTypeId!)! });
			if (this.canFetchUiModifications(portalId, requestTypeId)) {
				fetchUiModifications({
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					portalId: toNumber(portalId!)!,
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					requestTypeId: toNumber(requestTypeId!)!,
				});
			}
		}
	}

	render() {
		const { sideBarOpen } = this.state;
		const {
			isLoading,
			error,
			announcement,
			requestTypeName,
			portalName,
			isProjectSimplified,
			isProjectAdmin,
			projectId,
		} = this.props;
		const { requestTypeId, portalId } = this.props.match.params;
		return (
			<RequestCreateDumb
				requestTypeName={requestTypeName}
				projectId={projectId}
				portalName={portalName}
				loading={isLoading}
				announcement={announcement}
				requestTypeId={Number(requestTypeId)}
				portalId={Number(portalId)}
				sideBarOpen={sideBarOpen}
				toggleSidebar={this.toggleSideBar}
				isProjectSimplified={isProjectSimplified}
				isProjectAdmin={isProjectAdmin}
				{...error}
			/>
		);
	}
}

const selector = createSelector(
	(state: State, props: ExternalProps) =>
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		getRequestCreateOrError(state, props.match.params.portalId!, props.match.params.requestTypeId!),
	(state: State, props: ExternalProps) =>
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		getPortalAnnouncement(state, toNumber(props.match.params.portalId!)),
	(state: State, props: ExternalProps) =>
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		getProjectId(state, toNumber(props.match.params.portalId!)),
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	(state: State, props: ExternalProps) => getName(state, toNumber(props.match.params.portalId!)),
	(state: State, props: ExternalProps) =>
		getFormName(
			state,
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			toNumber(props.match.params.portalId!),
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			toNumber(props.match.params.requestTypeId!),
		),
	(state: State, props: ExternalProps) =>
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		getIsProjectSimplified(state, toNumber(props.match.params.portalId!)),
	(state: State, props: ExternalProps) =>
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		getCanAdministerProject(state, toNumber(props.match.params.portalId!)),

	(state: State) => getCanAdministerJIRA(state),
	(state: State) => isLoggedInSelector(state),
	(
		request,
		announcement,
		projectId,
		portalName,
		requestTypeName,
		isProjectSimplified,
		isProjectAdmin,
		isJiraAdmin,
		isLoggedIn,
	) => ({
		announcement,
		projectId,
		portalName,
		requestTypeName,
		isProjectSimplified,
		isProjectAdmin,
		isJiraAdmin,
		isLoggedIn,
		error: request && 'error' in request ? request : undefined,
		isLoading: !request,
	}),
);

export default connect(selector, {
	fetchRequestCreate: fetchRequestCreateAction,
	fetchUiModifications: fetchUiModificationsAction,
})(withExperience(ExperienceName.REQUEST_CREATE)(RequestCreatePage));
